/**
 * SizeBuilderMixed
 *
 * Tiny widget for the Admin Product form: two numeric fields, each with
 * their own unit dropdown (ft / in / cm / mm), and an "Add" button that
 * produces a canonical size string ("4ft x 6in") plus its sqft value.
 *
 * The central `sizeToSqft` helper in `lib/store.js` already parses the
 * mixed-unit format natively, so the catalog and cart math stay in sync.
 *
 * Admin-only by design — the user explicitly asked that staff NOT see
 * size inputs in the frontend (catalog choices only).
 */
import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { sizeToSqft } from '../../lib/store';
import { Plus } from 'lucide-react';

const UNITS = [
  { v: 'ft', label: 'ft' },
  { v: 'in', label: 'in' },
  { v: 'cm', label: 'cm' },
  { v: 'mm', label: 'mm' },
];

export default function SizeBuilderMixed({ onAdd }) {
  const [a, setA] = useState('');
  const [au, setAu] = useState('ft');
  const [b, setB] = useState('');
  const [bu, setBu] = useState('ft');

  const sizeStr = (a && b) ? `${a}${au} x ${b}${bu}` : '';
  const sqftVal = sizeStr ? sizeToSqft(sizeStr, '') : 0;

  const handleAdd = () => {
    if (!a || !b || sqftVal <= 0) return;
    const label = `${a}${au} × ${b}${bu}`;
    onAdd(sizeStr, Math.round(sqftVal * 100) / 100, label);
    setA(''); setB('');
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Input
        type="number"
        step="0.1"
        value={a}
        onChange={(e) => setA(e.target.value)}
        placeholder="4"
        className="w-16 h-8 text-sm"
        data-testid="size-builder-a"
      />
      <Select value={au} onValueChange={setAu}>
        <SelectTrigger className="w-16 h-8 text-xs" data-testid="size-builder-a-unit"><SelectValue /></SelectTrigger>
        <SelectContent>{UNITS.map((u) => <SelectItem key={u.v} value={u.v}>{u.label}</SelectItem>)}</SelectContent>
      </Select>
      <span className="text-xs text-[#8F9485] px-1">×</span>
      <Input
        type="number"
        step="0.1"
        value={b}
        onChange={(e) => setB(e.target.value)}
        placeholder="6"
        className="w-16 h-8 text-sm"
        data-testid="size-builder-b"
      />
      <Select value={bu} onValueChange={setBu}>
        <SelectTrigger className="w-16 h-8 text-xs" data-testid="size-builder-b-unit"><SelectValue /></SelectTrigger>
        <SelectContent>{UNITS.map((u) => <SelectItem key={u.v} value={u.v}>{u.label}</SelectItem>)}</SelectContent>
      </Select>
      <span className="text-[10px] text-[#5C6150] px-1 font-mono-data min-w-[80px]">
        {sqftVal > 0 ? `= ${sqftVal.toFixed(2)} sft` : ''}
      </span>
      <Button
        type="button"
        size="sm"
        className="h-8 bg-[#556B2F] hover:bg-[#465926] text-white"
        onClick={handleAdd}
        disabled={!a || !b || sqftVal <= 0}
        data-testid="size-builder-add"
      >
        <Plus className="w-3 h-3 mr-1" /> Add
      </Button>
    </div>
  );
}
